import { JSX, ReactNode } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { ScreenSize } from "../../Components";

export class Typography extends BaseComponent<ITypographyProps, ITypographyState>
{
    private resizeHandlerId = '';

    constructor(props: ITypographyProps)
    {
        super(props);
        this.state = {
            screenSize: 'expanded',
        };
    }

    public componentDidMount(): void
    {
        this.setState({
            screenSize: this.components.screenSize,
        });
        this.components.addResizeHandler(i => this.setState({ screenSize: i }), id => this.resizeHandlerId = id);
        this.checkSize();
    }

    public componentDidUpdate(prevProps: ITypographyProps): void
    {
        if (prevProps.size != this.props.size)
        {
            this.checkSize();
        }
    }

    private checkSize(): void
    {
        switch (this.props.role)
        {
            case 'display':
                this.validateSize('small', 'medium');
                break;
            case 'headline':
                this.validateSize('small', 'medium', 'large');
                break;
            case 'title':
                this.validateSize('small', 'medium', 'large');
                break;
            case 'body':
                this.validateSize('extraSmall', 'small', 'medium', 'large');
                break;
            case 'label':
                this.validateSize('small', 'medium', 'large', 'extraLarge');
                break;
            default:
                throw new Error('Not supported');
        }
    }

    private validateSize(...validSizes: TypographySize[]): void
    {
        const size = this.props.size || 'medium';
        if (!validSizes.includes(size))
        {
            throw new Error(`When Role is '${this.props.role}', Size must be either ${validSizes.map(i => `'${i}'`)}.`);
        }
    }

    public render(): JSX.Element
    {
        const role = this.props.role;
        const size = this.props.size || 'medium';
        const variant = this.state.screenSize;
        const Tag = this.props.variant ? this.props.variant : 'span';

        return (
            <Tag
                style={{
                    font: `var(--sd-system-typography-${role}-${size}_${variant}) var(--global-font-body)`,
                    display: this.props.inline ? 'inline' : 'block',
                }}
            >
                {this.props.children}
            </Tag>
        );
    }

    public componentWillUnmount(): void
    {
        this.components.removeResizeHandler(this.resizeHandlerId);
    }
}

export interface ITypographyProps
{
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
    children: ReactNode;
    role: TypographyRole;
    size?: TypographySize;
    inline?: boolean;

}

export interface ITypographyState
{
    screenSize: ScreenSize;
}

export type TypographyRole = 'display' | 'headline' | 'title' | 'body' | 'label';
export type TypographySize = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';
